import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Typography, useTheme, Box
} from "@mui/material";
import {formatCurrency} from "../utils/formatters";
import {LoadingSpinner} from "../utils/loading_spinner";
import {useOutletContext} from "react-router";

const StatusIndicator = ({ status, theme }) => {
    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'critical': return theme.palette.status.critical;
            case 'to review': return theme.palette.status.warning;
            case 'on track': return theme.palette.status.success;
            default: return theme.palette.status.neutral;
        }
    };

    return (
        <Box sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: getStatusColor(status),
            display: 'inline-block'
        }} />
    );
};

// TODO: Button optimize/view action
const OptimizeButton = ({ onClick, theme }) => (
    <button
        onClick={onClick}
        style={{
            backgroundColor: theme.palette.primary,
            color: theme.palette.status.success,
            border: 'none',
            borderRadius: 4,
            padding: '4px 12px',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            minWidth: '70px'
        }}
    >
        Optimize
    </button>
);

export const RecoverableMarginDetailTable = ({
    data, selectedLeak, loading
}) => {
    const theme = useTheme();
    const {navOptions, handleNavigate} = useOutletContext();

    if (!selectedLeak) {
        return (
            <Paper style={{...theme.palette.contentPanel, padding: 20, marginTop: 20}}>
                <Typography variant="h6" gutterBottom>
                    Opportunity Details - drill down/rollup
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{marginTop: 20}}>
                    Select a recoverable margin area from the chart above to view details
                </Typography>
            </Paper>
        );
    }

    const headerStyle = {
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold',
        padding: '12px 16px',
        borderBottom: '2px solid #ddd',
        position: 'sticky',
        top: 0,
        zIndex: 1
    };

    const cellStyle = {
        padding: '12px 16px',
        borderBottom: '1px solid #e0e0e0'
    };

    if (loading) {
        return (
            <Paper style={{...theme.palette.contentPanel, padding: 20, marginTop: 20}}>
                <Typography variant="h6" gutterBottom>
                    Opportunity Details - drill down/rollup
                </Typography>
                <LoadingSpinner message="Loading opportunity details..." />
            </Paper>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Paper style={{...theme.palette.contentPanel, padding: 20, marginTop: 20}}>
                <Typography variant="h6" gutterBottom>
                    Opportunity Details - drill down/rollup
                </Typography>
                <Typography variant="body2" style={{marginTop: 10, marginBottom: 20}}>
                    Selected: <strong>{selectedLeak.description}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    No detail records available for this profit leak category
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper style={{...theme.palette.contentPanel, padding: 20, marginTop: 20}}>
            <Typography variant="h6" gutterBottom>
                Opportunity Details - {selectedLeak.area}
            </Typography>
            
            <TableContainer style={{maxHeight: 600}}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerStyle}>
                                Area
                            </TableCell>
                            <TableCell sx={headerStyle}>
                                Profit Leak Description
                            </TableCell>
                            <TableCell sx={headerStyle} align="center">
                                Status
                            </TableCell>
                            <TableCell sx={headerStyle} align="right">
                                Total Margin Opportunity
                            </TableCell>
                            <TableCell sx={headerStyle} align="right">
                                Est. Recoverable Margin
                            </TableCell>
                            <TableCell sx={headerStyle} align="right">
                                % of Impact
                            </TableCell>
                            <TableCell sx={headerStyle} align="center">
                                # Accounts
                            </TableCell>
                            <TableCell sx={headerStyle} align="center">
                                # SKUs
                            </TableCell>
                            <TableCell sx={headerStyle} align="center">
                                # Sales Reps
                            </TableCell>
                            <TableCell sx={headerStyle} align="center">
                                Action
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, idx) => {
                            const totalMarginOpportunity = row.profitImpact || 0;
                            const estimatedRecoverableMargin = totalMarginOpportunity; // TODO: replace with the real format, but ignore for now.
                            const percentImpact = row.percentImpact || 0;
                            
                            return (
                                <TableRow key={row.id || idx} hover>
                                    <TableCell sx={cellStyle}>
                                        {row.area || 'N/A'}
                                    </TableCell>
                                    <TableCell sx={cellStyle}>
                                        {row.description || 'N/A'}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="center">
                                        <StatusIndicator status={row.status} theme={theme} />
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="right">
                                        {formatCurrency(totalMarginOpportunity)}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="right">
                                        {formatCurrency(estimatedRecoverableMargin)}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="right">
                                        {percentImpact}%
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="center">
                                        {row.accounts || 0}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="center">
                                        {row.skus || 0}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="center">
                                        {row.salesReps || 0}
                                    </TableCell>
                                    <TableCell sx={cellStyle} align="center">
                                        <OptimizeButton 
                                            onClick={() => handleNavigate(navOptions.optimize)}
                                            theme={theme}
                                        />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};
